import type { Locale } from '../i18n/types';

import saladsImage from '../assets/checkpoint-charly-garden.jpg';
import startersImage from '../assets/checkpoint-charly-garden01.jpg';
import mainsImage from '../assets/checkpoint-charly-interior01.jpg';
import dessertsImage from '../assets/checkpoint-charly -interior.jpg';
import drinksImage from '../assets/wall-decoration.jpg';

export type MenuPrice = {
  bgn: number;
  eur: number;
};

export type MenuItem = {
  name: string;
  description?: string;
  weight?: string;
  allergens?: string[];
  price: MenuPrice;
};

export type MenuGroup = {
  name: string;
  items: MenuItem[];
};

export type MenuAllergen = {
  code: string;
  name: string;
  description?: string;
};

export type MenuSection = {
  id: 'salads' | 'starters' | 'mains' | 'desserts' | 'wine' | 'alcohol' | 'drinks';
  title: string;
  subtitle?: string;
  image: {
    src: string;
    alt: string;
  };
  items?: MenuItem[];
  groups?: MenuGroup[];
};

export type MenuContent = {
  sections: MenuSection[];
};

export const MENU_FLIPBOOK_BASE_URL = 'https://menu.checkpointcharly.everbranch.co/main/latest';

export const MENU_PDF_FILENAMES: Record<Locale, string> = {
  bg: 'main_menu.pdf',
  en: 'main_menu_en.pdf',
};

export const MENU_FLIPBOOK_URLS: Record<Locale, string> = {
  bg: `${MENU_FLIPBOOK_BASE_URL}/index.html`,
  en: `${MENU_FLIPBOOK_BASE_URL}/index.html`,
};

export const MENU_ALLERGENS: Record<Locale, MenuAllergen[]> = {
  bg: [
    {
      code: '1',
      name: 'Зърнени култури, съдържащи глутен (пшеница, ръж, ечемик, овес, спелта)',
    },
    { code: '2', name: 'Ракообразни и продукти от тях' },
    { code: '3', name: 'Яйца и яйчни продукти' },
    { code: '4', name: 'Риба и рибни продукти' },
    { code: '5', name: 'Фъстъци и продукти от тях' },
    { code: '6', name: 'Соеви зърна и продукти от тях' },
    {
      code: '7',
      name: 'Мляко и млечни продукти (включително лактоза)',
    },
    {
      code: '8',
      name: 'Ядки (бадеми, лешници, орехи, кашу, пекан, бразилски орехи, шамфъстък, макадамия)',
    },
    { code: '9', name: 'Целина и продукти от нея' },
    { code: '10', name: 'Синап (горчица) и продукти от него' },
    { code: '11', name: 'Сусам и продукти от него' },
    {
      code: '12',
      name: 'Серен диоксид и сулфити (над 10 mg/kg или 10 mg/l)',
    },
    { code: '13', name: 'Лупина и продукти от нея' },
    { code: '14', name: 'Мекотели и продукти от тях' },
  ],
  en: [
    {
      code: '1',
      name: 'Cereals containing gluten (wheat, rye, barley, oats, spelt)',
    },
    { code: '2', name: 'Crustaceans and products thereof' },
    { code: '3', name: 'Eggs and egg products' },
    { code: '4', name: 'Fish and fish products' },
    { code: '5', name: 'Peanuts and peanut products' },
    { code: '6', name: 'Soybeans and soy products' },
    {
      code: '7',
      name: 'Milk and dairy products (including lactose)',
    },
    {
      code: '8',
      name: 'Tree nuts (almonds, hazelnuts, walnuts, cashews, pecans, Brazil nuts, pistachios, macadamia)',
    },
    { code: '9', name: 'Celery and celery products' },
    { code: '10', name: 'Mustard and mustard products' },
    { code: '11', name: 'Sesame seeds and products thereof' },
    {
      code: '12',
      name: 'Sulphur dioxide and sulphites (>10 mg/kg or 10 mg/l)' },
    { code: '13', name: 'Lupin and lupin products' },
    { code: '14', name: 'Molluscs and mollusc products' },
  ],
};

export const MENU_CONTENT: Record<Locale, MenuContent> = {
  bg: {
    sections: [
      {
        id: 'salads',
        title: 'Салати',
        image: {
          src: saladsImage,
          alt: 'Салата с пресни зеленчуци от CheckPoint Charly',
        },
        items: [
          {
            name: 'Салата от червено цвекло с ябълка, моркови, сусам, зехтин и лимон',
            weight: '250 г',
            allergens: ['11'],
            price: { bgn: 14.9, eur: 7.62 },
          },
          {
            name: 'Шопска салата',
            description: 'Домати, краставици, печени чушки, сирене и магданоз',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 15.8, eur: 8.08 },
          },
          {
            name: 'Лютика с кълцани чушки, домати, сирене, яйце, чесън и магданоз',
            weight: '250 г',
            allergens: ['3', '7'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Цедено кисело мляко с краставици, копър, чесън и орехи',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 16.8, eur: 8.59 },
          },
          {
            name: 'Печени пиперки с маринован патладжан, козе сирене, копър и чесън',
            weight: '250 г',
            allergens: ['9'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Кьопоолу с препечени чеснови филийки и зрели маслини',
            weight: '250 г',
            allergens: ['1'],
            price: { bgn: 17.8, eur: 9.1 },
          },
          {
            name: 'Катък с печени чушки, домати, орехи и хлебчета със зехтин',
            weight: '250 г',
            allergens: ['1', '7', '8'],
            price: { bgn: 17.6, eur: 9.0 },
          },
          {
            name: 'Магданозена салата с булгур и градински зеленчуци',
            weight: '250 г',
            allergens: ['1'],
            price: { bgn: 16.7, eur: 8.54 },
          },
          {
            name: 'Белени домати, прясно биволско сирене, маслини и босилеков дресинг',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 19.6, eur: 10.02 },
          },
          {
            name: 'Пукани люти чушки с чесън, магданоз и зехтин',
            weight: '100 г',
            price: { bgn: 10.2, eur: 5.22 },
          },
          {
            name: 'Микс от зелени салати с авокадо, круша, печени орехи и балсамов дресинг',
            weight: '250 г',
            allergens: ['8'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Пилешки филенца със сусам, салати и мус от нахут с печени чушки',
            weight: '250 г',
            allergens: ['1', '3', '4'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Спаначена салата с ябълки, пармезан, печени орехи и меден дресинг',
            weight: '250 г',
            allergens: ['7', '8'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Пъстра салата с пушени риби, яйце, черита, пикантни каперси и крутони',
            weight: '250 г',
            allergens: ['1', '3', '4'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Рукола със зелени салати, сушени домати, печено козе сирене с мед и орехи',
            weight: '250 г',
            allergens: ['7', '8'],
            price: { bgn: 17.6, eur: 9.0 },
          },
          {
            name: 'Скариди със сусам на грил, салати с авокадо и лимонов зехтин',
            weight: '250 г',
            allergens: ['2', '11'],
            price: { bgn: 19.8, eur: 10.2 },
          },
          {
            name: 'Цитрусова сьомга със салати, авокадо, яйце, крутони и пармезан',
            weight: '250 г',
            allergens: ['1', '4', '7'],
            price: { bgn: 19.8, eur: 10.2 },
          },
          {
            name: 'Супа на деня',
            weight: '350 г',
            price: { bgn: 11.9, eur: 6.08 },
          },
          {
            name: 'Порция хляб',
            weight: '150 г',
            allergens: ['1'],
            price: { bgn: 4.5, eur: 2.3 },
          },
        ],
      },
      {
        id: 'starters',
        title: 'Предястия',
        image: {
          src: startersImage,
          alt: 'Предястия от менюто на CheckPoint Charly',
        },
        items: [
          {
            name: 'Домашна баница с кисело мляко',
            weight: '250 г',
            allergens: ['1', '3', '7'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Палента със сирене и масло',
            description: 'Царевичен грис, овче сирене и масло',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 19.6, eur: 10.02 },
          },
          {
            name: 'Тарама хайвер с аншоа и препечен хляб',
            weight: '250 г',
            allergens: ['1', '4', '14'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Печен кашкавал с домати и босилек',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 22.8, eur: 11.66 },
          },
          {
            name: 'Смилянски боб на фурна със зеленчуци',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Печено краве сирене с орехи, фурми и сладко от сушени плодове',
            weight: '250 г',
            allergens: ['7', '8'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Хумус със сусамено хлебче',
            weight: '250 г',
            allergens: ['1', '11'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Спаначени кюфтенца с кисело мляко и копър',
            weight: '250 г',
            allergens: ['1', '3', '7'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Печен патладжан с прясно биволско сирене, домати и босилек',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Мезе от колбаси и сирена с плодове и печени орехи',
            weight: '150 г',
            allergens: ['7', '8'],
            price: { bgn: 24.6, eur: 12.58 },
          },
          {
            name: 'Крехки пилешки филенца с мащерка, чесън и картофено пюре',
            weight: '250 г',
            price: { bgn: 21.8, eur: 11.15 },
          },
          {
            name: 'Ароматно ризото с горски гъби, рукола и пармезан',
            weight: '250 г',
            allergens: ['7'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Печен Камамбер с круши в Порто, канела и печени орехи',
            weight: '200 г',
            allergens: ['7', '8'],
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Фуа гра с бърбън сос, сливи и сусам върху препечен хляб',
            weight: '200 г',
            allergens: ['1', '11'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Пушени и мариновани риби с масло, хлебче и маслинова тапенада',
            weight: '200 г',
            allergens: ['1', '4', '7'],
            price: { bgn: 24.8, eur: 12.68 },
          },
          {
            name: 'Пресни калмари със зелени салати, каперси и лимонов зехтин',
            weight: '200 г',
            allergens: ['1', '14'],
            price: { bgn: 24.6, eur: 12.58 },
          },
          {
            name: 'Сьомгови филенца с портокалов глаз, шафраново ризото и медено-горчичен сос',
            weight: '200 г',
            allergens: ['4', '10'],
            price: { bgn: 23.9, eur: 12.22 },
          },
          {
            name: 'Скариди в къри сос с кокосово мляко, ориз жасмин и пъстра салата',
            weight: '200 г',
            allergens: ['2'],
            price: { bgn: 24.9, eur: 12.73 },
          },
        ],
      },
      {
        id: 'mains',
        title: 'Основни ястия',
        image: {
          src: mainsImage,
          alt: 'Основно ястие от менюто на CheckPoint Charly',
        },
        items: [
          {
            name: 'Кюфтета от нахут с орехова майонеза и пъстра салата с репички',
            weight: '350 г',
            allergens: ['1', '8'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Сочни кюфтета от мляно месо с печен боб и кьопоолу',
            weight: '350 г',
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Пикантни кебапчета от мляно месо с доматена салца и печени чушки',
            weight: '350 г',
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Микс от наденички, картофена салата с кисели краставички и печени чушки',
            weight: '350 г',
            price: { bgn: 24.8, eur: 12.68 },
          },
          {
            name: 'Крехки свински котлети на скара с горски гъби и задушени картофи',
            weight: '350 г',
            price: { bgn: 28.9, eur: 14.78 },
          },
          {
            name: 'Шницел от свинско филе, сотирани картофи, яйце и кисели краставички',
            weight: '350 г',
            allergens: ['1', '3', '7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Сочни стекчета от свинско филе и мачкани картофи с рукола',
            weight: '350 г',
            price: { bgn: 29.9, eur: 15.29 },
          },
          {
            name: 'Шницел от пилешко филе с печени картофи, яйце и кисели краставички',
            weight: '350 г',
            allergens: ['1', '3', '7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Пилешки гърди в кокосово мляко с чили ризото и салата тиквички',
            weight: '350 г',
            allergens: ['7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Крехко пилешко филе в сос от черри домати, каперси и картофи с мащерка',
            weight: '350 г',
            price: { bgn: 29.6, eur: 15.13 },
          },
          {
            name: 'Агнешки кюфтета, магданозена салата с булгур, хумус и ливански хляб',
            weight: '350 г',
            allergens: ['1'],
            price: { bgn: 35.7, eur: 18.25 },
          },
          {
            name: 'Пикантно патешко филе в сос от пипери и картофи гретен със спанак',
            weight: '350 г',
            allergens: ['6', '7'],
            price: { bgn: 42.6, eur: 21.78 },
          },
          {
            name: 'Сочни патешки гърди с круши в Порто и канела със зеленчуково ризото',
            weight: '350 г',
            allergens: ['7'],
            price: { bgn: 42.9, eur: 21.93 },
          },
          {
            name: 'Пикантни телешки кюфтета с овче сирене, бял ориз и салата с кисело мляко',
            weight: '350 г',
            allergens: ['7'],
            price: { bgn: 35.7, eur: 18.25 },
          },
          {
            name: 'Пепър стек с манатарки в масло и картофи с мащерка',
            weight: '350 г',
            allergens: ['6', '7'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Телешки стекчета в сос от бърбън, кленов сироп, еспресо и картофено пюре',
            weight: '350 г',
            allergens: ['6', '7'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Филета от пъстърва с лимонов сос, задушени картофи и салата от тиквички',
            weight: '350 г',
            allergens: ['4'],
            price: { bgn: 26.7, eur: 13.65 },
          },
          {
            name: 'Лаврак на скара със сушени домати, сотирани картофи и зелена салата',
            weight: '350 г',
            allergens: ['4'],
            price: { bgn: 37.9, eur: 19.38 },
          },
          {
            name: 'Филе от сьомга с маслини, каперси, черри домати и печени тиквички',
            weight: '350 г',
            allergens: ['4', '6'],
            price: { bgn: 36.8, eur: 18.82 },
          },
          {
            name: 'Печена сьомга в лимонов сос с кокосово мляко, къри и жасминов ориз',
            weight: '350 г',
            allergens: ['2', '7', '11'],
            price: { bgn: 36.8, eur: 18.82 },
          },
          {
            name: 'Скариди с шафраново ризото, рукола и медено-сусамов дресинг',
            weight: '350 г',
            allergens: ['2', '7', '11'],
            price: { bgn: 32.8, eur: 16.77 },
          },
        ],
      },
      {
        id: 'desserts',
        title: 'Десерти',
        image: {
          src: dessertsImage,
          alt: 'Десерт от менюто на CheckPoint Charly',
        },
        items: [
          {
            name: 'Крем карамел',
            weight: '180 г',
            allergens: ['3', '7'],
            price: { bgn: 10.8, eur: 5.52 },
          },
          {
            name: 'Мус от черен шоколад и ванилов крем',
            weight: '180 г',
            allergens: ['3', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Шоколадово суфле със сметанов сладолед',
            weight: '180 г',
            allergens: ['1', '3', '7', '8'],
            price: { bgn: 12.9, eur: 6.6 },
          },
          {
            name: 'Медено сирене крема със сушени плодове и „Гран Марние"',
            weight: '180 г',
            allergens: ['1', '7'],
            price: { bgn: 12.8, eur: 6.54 },
          },
          {
            name: 'Тирамису',
            weight: '180 г',
            allergens: ['1', '3', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Чийзкейк',
            weight: '180 г',
            allergens: ['1', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Баклава',
            weight: '180 г',
            allergens: ['1', '8'],
            price: { bgn: 12.8, eur: 6.54 },
          },
          {
            name: 'Лек крем от кисело мляко с ягоди / малини',
            weight: '180 г',
            allergens: ['7'],
            price: { bgn: 10.8, eur: 5.52 },
          },
        ],
      },
      {
        id: 'wine',
        title: 'Винена листа',
        subtitle: 'Подбрани вина на чаша и бутилка',
        image: {
          src: drinksImage,
          alt: 'Винена селекция в CheckPoint Charly',
        },
        groups: [
          {
            name: 'Български бели вина',
            items: [
              {
                name: 'Чаша бяло вино 200 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Гарафа бяло вино 500 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Совиньон Блан & Вионие 375 мл',
                description: 'Капатово',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Совиньон Блан & Вионие 750 мл',
                description: 'Капатово',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Блек С Уайт 375 мл',
                description: 'Санта Сара',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Блек С Уайт 750 мл',
                description: 'Санта Сара',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Тамянка 750 мл',
                description: 'Винарна Братанов',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Совиньон Блан 750 мл',
                description: 'Санта Сара',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Врачански мискет 750 мл',
                description: 'Старо Оряхово',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Шардоне Барик 750 мл',
                description: 'Капатово',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Шардоне 750 мл',
                description: 'Санта Сара',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Шардоне Барел Левент 750 мл',
                description: 'ВК Русе',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Траминер & Врачански мискет 750 мл',
                description: 'ВК Русе',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Траминер 750 мл',
                description: 'Санта Сара',
                price: { bgn: 68, eur: 34.77 },
              },
            ],
          },
          {
            name: 'Пенливи вина',
            items: [
              {
                name: 'Просеко Джал’оро Екстра Драй 375 мл',
                description: 'Италия',
                price: { bgn: 38, eur: 19.43 },
              },
              {
                name: 'Просеко Джал’оро Екстра Драй 750 мл',
                description: 'Италия',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Брут Премие, Луи Рьодерер 750 мл',
                description: 'Франция, Шампан',
                price: { bgn: 240, eur: 122.71 },
              },
            ],
          },
          {
            name: 'Бели вина от света',
            items: [
              {
                name: 'Пино Гриджо Кантина ди Соаве I.G.T. 375 мл',
                description: 'Венето, Италия',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Пино Гриджо Кантина ди Соаве I.G.T. 750 мл',
                description: 'Венето, Италия',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Совиньон Блан Франсоа Люртон Les Fumées Blanches 375 мл',
                description: 'Лангедок, Франция',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Совиньон Блан Франсоа Люртон Les Fumées Blanches 750 мл',
                description: 'Лангедок, Франция',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Преамбюл Пикпу де Пине 750 мл',
                description: 'Франция',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Грюнер Велтлинер Хаусвайн 750 мл',
                description: 'Weingut Bründlmayer, Австрия',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Шабли Жозеф Дроен 750 мл',
                description: 'Франция',
                price: { bgn: 128, eur: 65.45 },
              },
              {
                name: 'Бабич Совиньон Блан 750 мл',
                description: 'Марлборо, Нова Зеландия',
                price: { bgn: 68, eur: 34.77 },
              },
            ],
          },
          {
            name: 'Вина розе',
            items: [
              {
                name: 'Чаша розе 200 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Гарафа розе 500 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Розе Гренаш и Мурведър 375 мл',
                description: 'Капатово',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Розе Гренаш и Мурведър 750 мл',
                description: 'Капатово',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Блек С Розе 375 мл',
                description: 'Санта Сара',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Блек С Розе 750 мл',
                description: 'Санта Сара',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Розе Левент 750 мл',
                description: 'ВК Русе',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Шато Д’астрос Амфора 750 мл',
                description: 'Кот дю Прованс, Франция',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Розе Бай От, Домейн Отт 750 мл',
                description: 'Прованс, Франция',
                price: { bgn: 96, eur: 49.08 },
              },
            ],
          },
          {
            name: 'Червени вина от България',
            items: [
              {
                name: 'Чаша червено вино 200 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Гарафа червено вино 500 мл',
                description: 'Mezzek / Капатово',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Капатово 375 мл',
                description: 'Шираз, Пети Вердо, Примитиво',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Капатово 750 мл',
                description: 'Шираз, Пети Вердо, Примитиво',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Блек С Ред 375 мл',
                description: 'Санта Сара',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Блек С Ред 750 мл',
                description: 'Санта Сара',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Уникато Мелник 750 мл',
                description: 'Уникато',
                price: { bgn: 66, eur: 33.75 },
              },
              {
                name: 'Бин 40 Каберне Совиньон 750 мл',
                description: 'Санта Сара',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Енира Резерва 750 мл',
                description: 'Беса Валей',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Боровица Гъмза 750 мл',
                description: 'Боровица',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Санта Сара Бин 41 Мерло 750 мл',
                description: 'Санта Сара',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Мерло Солитер 750 мл',
                description: 'ВИ Домейн Бояр',
                price: { bgn: 98, eur: 50.1 },
              },
              {
                name: 'Мерло Пепър Гардън 750 мл',
                description: 'Great Terroirs, Боровица',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Мерло & Каберне Совиньон Резерва 750 мл',
                description: 'Яйловци, Ямптиково',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Мавруд Резерва 750 мл',
                description: 'Загрей',
                price: { bgn: 62, eur: 31.7 },
              },
            ],
          },
          {
            name: 'Червени вина от света',
            items: [
              {
                name: 'Каберне Совиньон Casillero del Diablo 750 мл',
                description: 'Concha y Toro, Чили',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Сангаетано Примитиво ди Мандурия 750 мл',
                description: 'Италия',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Garrigue Мерло и Сира 750 мл',
                description: 'Франция',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Columbia Crest H3 Мерло 750 мл',
                description: 'САЩ',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Катена Малбек 750 мл',
                description: 'Аржентина',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Пино Ноар Лафоре 750 мл',
                description: 'Франция',
                price: { bgn: 118, eur: 60.33 },
              },
            ],
          },
        ],
      },
      {
        id: 'alcohol',
        title: 'Алкохол',
        image: {
          src: mainsImage,
          alt: 'Бар с алкохолни напитки в CheckPoint Charly',
        },
        groups: [
          {
            name: 'Ракия 50 мл',
            items: [
              { name: 'Българска мускатова', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Сунгурларска специална', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Троянска сливова отлежала', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Троянска дюлева', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Капатово гроздова', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Бургас 63 перлова', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Ципуро мавро', price: { bgn: 6.9, eur: 3.53 } },
            ],
          },
          {
            name: 'Ракии „Бистра"',
            items: [
              { name: 'Гроздова', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Гроздова лежала', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Грена медена гроздова', price: { bgn: 7.9, eur: 4.04 } },
            ],
          },
          {
            name: 'Водка 50 мл',
            items: [
              { name: 'Czaraska', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Absolut', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Finlandia', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Russian Standard', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Reyka', price: { bgn: 12.8, eur: 6.54 } },
              { name: 'Beluga', price: { bgn: 15.8, eur: 8.08 } },
            ],
          },
          {
            name: 'Джин 50 мл',
            items: [
              { name: 'Beefeater', price: { bgn: 5.9, eur: 3.02 } },
              { name: "Gordon's", price: { bgn: 5.9, eur: 3.02 } },
              { name: "Bombay 'Sapphire'", price: { bgn: 9.8, eur: 5.01 } },
            ],
          },
          {
            name: 'Анасонови напитки',
            items: [
              { name: 'Ouzo 12', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Ouzo 12 – 200 мл', price: { bgn: 26.0, eur: 13.29 } },
              { name: 'Ouzo Plomari', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Pernod', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Ricard', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Absente', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Шотландско уиски 50 мл',
            items: [
              { name: 'Johnnie Walker Red Label', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Johnnie Walker Black Label', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'J&B', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Famous Grouse', price: { bgn: 7.9, eur: 4.04 } },
              { name: "Ballantine's", price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Dimple 15 YO', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Cardhu 12 YO', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Chivas Regal 12 YO', price: { bgn: 18.0, eur: 9.2 } },
            ],
          },
          {
            name: 'Ирландско уиски 50 мл',
            items: [
              { name: 'Bushmills', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Tullamore Dew', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Jameson', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Glenfarclas 10 YO', price: { bgn: 22.0, eur: 11.25 } },
            ],
          },
          {
            name: 'Бърбън 50 мл',
            items: [
              { name: 'Jim Beam', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Four Roses', price: { bgn: 7.9, eur: 4.04 } },
              { name: "Jack Daniel's", price: { bgn: 12.5, eur: 6.39 } },
              { name: "Maker's Mark", price: { bgn: 12.5, eur: 6.39 } },
            ],
          },
          {
            name: 'Малцово уиски 50 мл',
            items: [
              { name: 'Glenfiddich 12 YO', price: { bgn: 20.0, eur: 10.23 } },
              { name: 'Talisker 10 YO', price: { bgn: 22.0, eur: 11.25 } },
              { name: 'Balvenie 12 YO', price: { bgn: 28.0, eur: 14.32 } },
              { name: 'Yamazakura', price: { bgn: 22.0, eur: 11.25 } },
            ],
          },
          {
            name: 'Ром 50 мл',
            items: [
              { name: 'Havana Club 3 YO', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Havana Club 7 YO', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Коктейли 200 мл',
            items: [
              { name: 'Aperol Spritz', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Campari & Tonik', price: { bgn: 9.5, eur: 4.86 } },
            ],
          },
          {
            name: 'Ликьори 50 мл',
            items: [
              { name: 'Baileys', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Disaronno Amaretto', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Grand Marnier', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Martini', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Campari', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Drambuie', price: { bgn: 15.0, eur: 7.67 } },
              { name: 'Fernet-Branka', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Branca Menta', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Yegermeister', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Limoncello di Capri', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Benedictine', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Текила 50 мл',
            items: [{ name: 'Sauza Gold', price: { bgn: 7.5, eur: 3.83 } }],
          },
          {
            name: 'Коняк 50 мл',
            items: [
              { name: 'Courvoisier VS', price: { bgn: 16.0, eur: 8.18 } },
              { name: 'Бренди Плиска XO', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Арманяк VSOP', price: { bgn: 18.0, eur: 9.2 } },
            ],
          },
          {
            name: 'Бира',
            items: [
              { name: 'Starobrno наливна 300 мл', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Starobrno наливна 500 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Heineken наливна 250 мл', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Heineken наливна 400 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Загорка 330 мл', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Heineken 330 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Heineken 0.0 330 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Stolichno Weiss 330 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Birra Moretti 330 мл', price: { bgn: 6.9, eur: 3.53 } },
            ],
          },
        ],
      },
      {
        id: 'drinks',
        title: 'Напитки',
        image: {
          src: drinksImage,
          alt: 'Безалкохолни и топли напитки в CheckPoint Charly',
        },
        groups: [
          {
            name: 'Безалкохолни',
            items: [
              { name: 'Кока-кола, фанта, тоник, сода 250 мл', price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Минерална вода 500 мл', price: { bgn: 3.9, eur: 1.99 } },
              { name: 'Минерална вода 1 л', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Минерална вода Perrier 330 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Минерална вода Perrier 750 мл', price: { bgn: 9.9, eur: 5.06 } },
              { name: 'Айрян 250 мл', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Натурален сок 250 мл', price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Фреш 250 мл', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Цитронада 250 мл', price: { bgn: 7.9, eur: 4.04 } },
            ],
          },
          {
            name: 'Топли',
            items: [
              { name: "Кафе 'ILLY' 30 мл", price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Капучино 100 мл', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Чай “Delmar Te” 200 мл', price: { bgn: 4.9, eur: 2.51 } },
            ],
          },
        ],
      },
    ],
  },
  en: {
    sections: [
      {
        id: 'salads',
        title: 'Salads',
        image: {
          src: saladsImage,
          alt: 'Fresh salad bowl at CheckPoint Charly',
        },
        items: [
          {
            name: 'Red beet salad with apple, carrots, sesame, olive oil & lemon',
            weight: '250 g',
            allergens: ['11'],
            price: { bgn: 14.9, eur: 7.62 },
          },
          {
            name: 'Shopska salad',
            description: 'Tomatoes, cucumbers, roasted peppers, sirene cheese & parsley',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 15.8, eur: 8.08 },
          },
          {
            name: 'Roasted pepper lyutenitsa with tomatoes, cheese, egg, garlic & parsley',
            weight: '250 g',
            allergens: ['3', '7'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Strained yogurt with cucumbers, dill, garlic & walnuts',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 16.8, eur: 8.59 },
          },
          {
            name: 'Roasted peppers and marinated eggplant with goat cheese, dill & garlic',
            weight: '250 g',
            allergens: ['9'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Kyopoolu spread with toasted garlic bread & ripe olives',
            weight: '250 g',
            allergens: ['1'],
            price: { bgn: 17.8, eur: 9.1 },
          },
          {
            name: 'Katak spread with roasted peppers, tomatoes, walnuts & olive-oil bread bites',
            weight: '250 g',
            allergens: ['1', '7', '8'],
            price: { bgn: 17.6, eur: 9.0 },
          },
          {
            name: 'Parsley bulgur salad with garden vegetables',
            weight: '250 g',
            allergens: ['1'],
            price: { bgn: 16.7, eur: 8.54 },
          },
          {
            name: 'Peeled tomatoes with fresh buffalo cheese, olives & basil dressing',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 19.6, eur: 10.02 },
          },
          {
            name: 'Blistered hot peppers with garlic, parsley & olive oil',
            weight: '100 g',
            price: { bgn: 10.2, eur: 5.22 },
          },
          {
            name: 'Mixed greens with avocado, pear, toasted walnuts & balsamic dressing',
            weight: '250 g',
            allergens: ['8'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Sesame chicken fillets with salads and roasted pepper chickpea mousse',
            weight: '250 g',
            allergens: ['1', '3', '4'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Spinach salad with apples, Parmesan, toasted walnuts & honey dressing',
            weight: '250 g',
            allergens: ['7', '8'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Smoked fish salad with egg, cherry tomatoes, spicy capers & croutons',
            weight: '250 g',
            allergens: ['1', '3', '4'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Arugula with sun-dried tomatoes, baked goat cheese, honey & walnuts',
            weight: '250 g',
            allergens: ['7', '8'],
            price: { bgn: 17.6, eur: 9.0 },
          },
          {
            name: 'Grilled sesame shrimp with avocado salad & lemon olive oil',
            weight: '250 g',
            allergens: ['2', '11'],
            price: { bgn: 19.8, eur: 10.2 },
          },
          {
            name: 'Citrus salmon salad with avocado, egg, croutons & Parmesan',
            weight: '250 g',
            allergens: ['1', '4', '7'],
            price: { bgn: 19.8, eur: 10.2 },
          },
          {
            name: 'Soup of the day',
            weight: '350 g',
            price: { bgn: 11.9, eur: 6.08 },
          },
          {
            name: 'Bread basket',
            weight: '150 g',
            allergens: ['1'],
            price: { bgn: 4.5, eur: 2.3 },
          },
        ],
      },
      {
        id: 'starters',
        title: 'Starters',
        image: {
          src: startersImage,
          alt: 'Starter selection from the CheckPoint Charly menu',
        },
        items: [
          {
            name: 'Homemade banitsa with yogurt',
            weight: '250 g',
            allergens: ['1', '3', '7'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Cornmeal polenta with sheep cheese & butter',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 19.6, eur: 10.02 },
          },
          {
            name: 'Tarama caviar with anchovy, red onion & toasted bread',
            weight: '250 g',
            allergens: ['1', '4', '14'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Baked kashkaval cheese with tomatoes & basil',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 22.8, eur: 11.66 },
          },
          {
            name: 'Smilyan beans roasted with vegetables',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Baked cow cheese with walnuts, dates & dried-fruit jam',
            weight: '250 g',
            allergens: ['7', '8'],
            price: { bgn: 18.6, eur: 9.51 },
          },
          {
            name: 'Hummus with sesame bread',
            weight: '250 g',
            allergens: ['1', '11'],
            price: { bgn: 17.9, eur: 9.15 },
          },
          {
            name: 'Spinach fritters with yogurt-dill sauce',
            weight: '250 g',
            allergens: ['1', '3', '7'],
            price: { bgn: 18.9, eur: 9.66 },
          },
          {
            name: 'Roasted eggplant with fresh buffalo cheese, tomatoes & basil',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Bulgarian cured meats & cheeses with fruit and roasted walnuts',
            weight: '150 g',
            allergens: ['7', '8'],
            price: { bgn: 24.6, eur: 12.58 },
          },
          {
            name: 'Tender chicken fillets with thyme, garlic & mashed potatoes',
            weight: '250 g',
            price: { bgn: 21.8, eur: 11.15 },
          },
          {
            name: 'Herb risotto with wild mushrooms, arugula & Parmesan',
            weight: '250 g',
            allergens: ['7'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Baked Camembert with Port-poached pears, cinnamon & roasted walnuts',
            weight: '200 g',
            allergens: ['7', '8'],
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Foie gras with bourbon sauce, plums & sesame on toasted bread',
            weight: '200 g',
            allergens: ['1', '11'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Smoked & marinated fish with butter, bread & olive tapenade',
            weight: '200 g',
            allergens: ['1', '4', '7'],
            price: { bgn: 24.8, eur: 12.68 },
          },
          {
            name: 'Fresh calamari with greens, spicy capers & lemon olive oil',
            weight: '200 g',
            allergens: ['1', '14'],
            price: { bgn: 24.6, eur: 12.58 },
          },
          {
            name: 'Salmon fillets with orange glaze, saffron risotto & honey-mustard sauce',
            weight: '200 g',
            allergens: ['4', '10'],
            price: { bgn: 23.9, eur: 12.22 },
          },
          {
            name: 'Shrimp in curry sauce with coconut milk, jasmine rice & rainbow salad',
            weight: '200 g',
            allergens: ['2'],
            price: { bgn: 24.9, eur: 12.73 },
          },
        ],
      },
      {
        id: 'mains',
        title: 'Main courses',
        image: {
          src: mainsImage,
          alt: 'Main course served at CheckPoint Charly',
        },
        items: [
          {
            name: 'Chickpea patties with walnut mayo & rainbow radish salad',
            weight: '350 g',
            allergens: ['1', '8'],
            price: { bgn: 21.6, eur: 11.04 },
          },
          {
            name: 'Juicy minced meat patties with baked beans & kyopoolu',
            weight: '350 g',
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Spicy minced meat kebapche with tomato relish & roasted peppers',
            weight: '350 g',
            price: { bgn: 23.8, eur: 12.17 },
          },
          {
            name: 'Sausage trio with potato salad, pickles & roasted peppers',
            weight: '350 g',
            price: { bgn: 24.8, eur: 12.68 },
          },
          {
            name: 'Grilled pork cutlets with wild mushrooms & braised potatoes',
            weight: '350 g',
            price: { bgn: 28.9, eur: 14.78 },
          },
          {
            name: 'Pork schnitzel with sautéed potatoes, egg & pickles',
            weight: '350 g',
            allergens: ['1', '3', '7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Seared pork medallions with smashed potatoes & arugula',
            weight: '350 g',
            price: { bgn: 29.9, eur: 15.29 },
          },
          {
            name: 'Chicken schnitzel with roasted potatoes, egg & pickles',
            weight: '350 g',
            allergens: ['1', '3', '7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Chicken breast in coconut milk with chili risotto & zucchini salad',
            weight: '350 g',
            allergens: ['7'],
            price: { bgn: 29.8, eur: 15.24 },
          },
          {
            name: 'Chicken fillet in cherry tomato sauce with capers & thyme potatoes',
            weight: '350 g',
            price: { bgn: 29.6, eur: 15.13 },
          },
          {
            name: 'Lamb kofta with parsley bulgur, hummus & Lebanese bread',
            weight: '350 g',
            allergens: ['1'],
            price: { bgn: 35.7, eur: 18.25 },
          },
          {
            name: 'Spicy duck breast with pepper sauce & spinach potato gratin',
            weight: '350 g',
            allergens: ['6', '7'],
            price: { bgn: 42.6, eur: 21.78 },
          },
          {
            name: 'Duck breast with pears in Port, cinnamon & vegetable risotto',
            weight: '350 g',
            allergens: ['7'],
            price: { bgn: 42.9, eur: 21.93 },
          },
          {
            name: 'Spiced veal meatballs with sheep cheese, rice & yogurt salad',
            weight: '350 g',
            allergens: ['7'],
            price: { bgn: 35.7, eur: 18.25 },
          },
          {
            name: 'Pepper steak with chanterelles in butter & thyme potatoes',
            weight: '350 g',
            allergens: ['6', '7'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Beef medallions in bourbon-maple-espresso sauce with mashed potatoes',
            weight: '350 g',
            allergens: ['6', '7'],
            price: { bgn: 55.9, eur: 28.58 },
          },
          {
            name: 'Trout fillets with lemon sauce, braised potatoes & zucchini salad',
            weight: '350 g',
            allergens: ['4'],
            price: { bgn: 26.7, eur: 13.65 },
          },
          {
            name: 'Grilled sea bass with sun-dried tomatoes, sautéed potatoes & green salad',
            weight: '350 g',
            allergens: ['4'],
            price: { bgn: 37.9, eur: 19.38 },
          },
          {
            name: 'Salmon fillet with olives, capers, cherry tomatoes & roasted zucchini',
            weight: '350 g',
            allergens: ['4', '6'],
            price: { bgn: 36.8, eur: 18.82 },
          },
          {
            name: 'Baked salmon in lemon sauce with coconut milk, curry & jasmine rice',
            weight: '350 g',
            allergens: ['2', '7', '11'],
            price: { bgn: 36.8, eur: 18.82 },
          },
          {
            name: 'Shrimp with saffron risotto, arugula & honey-sesame dressing',
            weight: '350 g',
            allergens: ['2', '7', '11'],
            price: { bgn: 32.8, eur: 16.77 },
          },
        ],
      },
      {
        id: 'desserts',
        title: 'Desserts',
        image: {
          src: dessertsImage,
          alt: 'Dessert served at CheckPoint Charly',
        },
        items: [
          {
            name: 'Crème caramel',
            weight: '180 g',
            allergens: ['3', '7'],
            price: { bgn: 10.8, eur: 5.52 },
          },
          {
            name: 'Dark chocolate mousse with vanilla cream',
            weight: '180 g',
            allergens: ['3', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Chocolate soufflé with whipped ice cream',
            weight: '180 g',
            allergens: ['1', '3', '7', '8'],
            price: { bgn: 12.9, eur: 6.6 },
          },
          {
            name: 'Honey cheese crème with dried fruit & Grand Marnier',
            weight: '180 g',
            allergens: ['1', '7'],
            price: { bgn: 12.8, eur: 6.54 },
          },
          {
            name: 'Tiramisu',
            weight: '180 g',
            allergens: ['1', '3', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Cheesecake',
            weight: '180 g',
            allergens: ['1', '7'],
            price: { bgn: 11.8, eur: 6.03 },
          },
          {
            name: 'Baklava',
            weight: '180 g',
            allergens: ['1', '8'],
            price: { bgn: 12.8, eur: 6.54 },
          },
          {
            name: 'Light yogurt cream with strawberries or raspberries',
            weight: '180 g',
            allergens: ['7'],
            price: { bgn: 10.8, eur: 5.52 },
          },
        ],
      },
      {
        id: 'wine',
        title: 'Wine List',
        subtitle: 'Curated selection of wines by the glass and bottle',
        image: {
          src: drinksImage,
          alt: 'Wine selection at CheckPoint Charly',
        },
        groups: [
          {
            name: 'Bulgarian white wines',
            items: [
              {
                name: 'Glass of white wine 200 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Carafe of white wine 500 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Sauvignon Blanc & Viognier 375 ml',
                description: 'Kapatovo',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Sauvignon Blanc & Viognier 750 ml',
                description: 'Kapatovo',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Blek C White 375 ml',
                description: 'Santa Sara',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Blek C White 750 ml',
                description: 'Santa Sara',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Tamyanka 750 ml',
                description: 'Bratanov Winery',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Sauvignon Blanc 750 ml',
                description: 'Santa Sara',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Vratsa Muscat 750 ml',
                description: 'Staro Oryahovo',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Chardonnay Barrique 750 ml',
                description: 'Kapatovo',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Chardonnay 750 ml',
                description: 'Santa Sara',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Chardonnay Barrel Levent 750 ml',
                description: 'VK Ruse',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Traminer & Vratsa Muscat 750 ml',
                description: 'VK Ruse',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Traminer 750 ml',
                description: 'Santa Sara',
                price: { bgn: 68, eur: 34.77 },
              },
            ],
          },
          {
            name: 'Sparkling wines',
            items: [
              {
                name: "Prosecco Djal'oro Extra Dry 375 ml",
                description: 'Italy',
                price: { bgn: 38, eur: 19.43 },
              },
              {
                name: "Prosecco Djal'oro Extra Dry 750 ml",
                description: 'Italy',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Brut Premier, Louis Roederer 750 ml',
                description: 'France, Champagne',
                price: { bgn: 240, eur: 122.71 },
              },
            ],
          },
          {
            name: 'White wines from around the world',
            items: [
              {
                name: 'Pinot Grigio Cantina di Soave I.G.T. 375 ml',
                description: 'Veneto, Italy',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Pinot Grigio Cantina di Soave I.G.T. 750 ml',
                description: 'Veneto, Italy',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Sauvignon Blanc François Lurton Les Fumées Blanches 375 ml',
                description: 'Languedoc, France',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Sauvignon Blanc François Lurton Les Fumées Blanches 750 ml',
                description: 'Languedoc, France',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Préambule Picpoul de Pinet 750 ml',
                description: 'France',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Grüner Veltliner Hauswein 750 ml',
                description: 'Weingut Bründlmayer, Austria',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Chablis Joseph Drouhin 750 ml',
                description: 'France',
                price: { bgn: 128, eur: 65.45 },
              },
              {
                name: 'Babich Sauvignon Blanc 750 ml',
                description: 'Marlborough, New Zealand',
                price: { bgn: 68, eur: 34.77 },
              },
            ],
          },
          {
            name: 'Rosé wines',
            items: [
              {
                name: 'Glass of rosé 200 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Carafe of rosé 500 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Rosé Grenache & Mourvèdre 375 ml',
                description: 'Kapatovo',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Rosé Grenache & Mourvèdre 750 ml',
                description: 'Kapatovo',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Blek C Rosé 375 ml',
                description: 'Santa Sara',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Blek C Rosé 750 ml',
                description: 'Santa Sara',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Rosé Levent 750 ml',
                description: 'VK Ruse',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: "Château d'Astros Amphora 750 ml",
                description: 'Côtes de Provence, France',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Rosé By Ott, Domaines Ott 750 ml',
                description: 'Provence, France',
                price: { bgn: 96, eur: 49.08 },
              },
            ],
          },
          {
            name: 'Red wines from Bulgaria',
            items: [
              {
                name: 'Glass of red wine 200 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 9.9, eur: 5.06 },
              },
              {
                name: 'Carafe of red wine 500 ml',
                description: 'Mezzek / Kapatovo',
                price: { bgn: 24, eur: 12.27 },
              },
              {
                name: 'Kapatovo 375 ml',
                description: 'Shiraz, Petit Verdot, Primitivo',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Kapatovo 750 ml',
                description: 'Shiraz, Petit Verdot, Primitivo',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Blek C Red 375 ml',
                description: 'Santa Sara',
                price: { bgn: 32, eur: 16.36 },
              },
              {
                name: 'Blek C Red 750 ml',
                description: 'Santa Sara',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Unikato Melnik 750 ml',
                description: 'Unikato',
                price: { bgn: 66, eur: 33.75 },
              },
              {
                name: 'Bin 40 Cabernet Sauvignon 750 ml',
                description: 'Santa Sara',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Enira Reserva 750 ml',
                description: 'Bessa Valley',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Borovitza Gamza 750 ml',
                description: 'Borovitza',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Santa Sara Bin 41 Merlot 750 ml',
                description: 'Santa Sara',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Merlot Solitaire 750 ml',
                description: 'VIN Domaine Boyar',
                price: { bgn: 98, eur: 50.1 },
              },
              {
                name: 'Merlot Pepper Garden 750 ml',
                description: 'Great Terroirs, Borovitza',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Merlot & Cabernet Sauvignon Reserve 750 ml',
                description: 'Reservе Yailovtsi, Yampetkovo',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Mavrud Reserva 750 ml',
                description: 'Zagreus',
                price: { bgn: 62, eur: 31.7 },
              },
            ],
          },
          {
            name: 'Red wines from around the world',
            items: [
              {
                name: 'Cabernet Sauvignon Casillero del Diablo 750 ml',
                description: 'Concha y Toro, Chile',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Sangaetano Primitivo di Manduria 750 ml',
                description: 'Italy',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Garrigue Merlot & Syrah 750 ml',
                description: 'France',
                price: { bgn: 62, eur: 31.7 },
              },
              {
                name: 'Columbia Crest H3 Merlot 750 ml',
                description: 'USA',
                price: { bgn: 82, eur: 41.93 },
              },
              {
                name: 'Catena Malbec 750 ml',
                description: 'Argentina',
                price: { bgn: 68, eur: 34.77 },
              },
              {
                name: 'Pinot Noir Laforêt 750 ml',
                description: 'France',
                price: { bgn: 118, eur: 60.33 },
              },
            ],
          },
        ],
      },
      {
        id: 'alcohol',
        title: 'Alcohol',
        image: {
          src: mainsImage,
          alt: 'Selection of spirits at CheckPoint Charly',
        },
        groups: [
          {
            name: 'Rakija 50 ml',
            items: [
              { name: 'Bulgarian Muscat rakija', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Sungurlare special rakija', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Troyan aged plum rakija', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Troyan quince rakija', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Kapatovo grape rakija', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Burgas 63 Pearl', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Tsipouro Mavro', price: { bgn: 6.9, eur: 3.53 } },
            ],
          },
          {
            name: '“Bistra” rakija',
            items: [
              { name: 'Grape', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Aged grape', price: { bgn: 8.9, eur: 4.55 } },
              { name: 'Spiced honey grape', price: { bgn: 7.9, eur: 4.04 } },
            ],
          },
          {
            name: 'Vodka 50 ml',
            items: [
              { name: 'Czaraska', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Absolut', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Finlandia', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Russian Standard', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Reyka', price: { bgn: 12.8, eur: 6.54 } },
              { name: 'Beluga', price: { bgn: 15.8, eur: 8.08 } },
            ],
          },
          {
            name: 'Gin 50 ml',
            items: [
              { name: 'Beefeater', price: { bgn: 5.9, eur: 3.02 } },
              { name: "Gordon's", price: { bgn: 5.9, eur: 3.02 } },
              { name: "Bombay Sapphire", price: { bgn: 9.8, eur: 5.01 } },
            ],
          },
          {
            name: 'Anise spirits',
            items: [
              { name: 'Ouzo 12', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Ouzo 12 – 200 ml bottle', price: { bgn: 26.0, eur: 13.29 } },
              { name: 'Ouzo Plomari', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Pernod', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Ricard', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Absente', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Scottish whisky 50 ml',
            items: [
              { name: 'Johnnie Walker Red Label', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Johnnie Walker Black Label', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'J&B', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Famous Grouse', price: { bgn: 7.9, eur: 4.04 } },
              { name: "Ballantine's", price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Dimple 15 YO', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Cardhu 12 YO', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Chivas Regal 12 YO', price: { bgn: 18.0, eur: 9.2 } },
            ],
          },
          {
            name: 'Irish whiskey 50 ml',
            items: [
              { name: 'Bushmills', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Tullamore Dew', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Jameson', price: { bgn: 8.5, eur: 4.35 } },
              { name: 'Glenfarclas 10 YO', price: { bgn: 22.0, eur: 11.25 } },
            ],
          },
          {
            name: 'Bourbon 50 ml',
            items: [
              { name: 'Jim Beam', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Four Roses', price: { bgn: 7.9, eur: 4.04 } },
              { name: "Jack Daniel's", price: { bgn: 12.5, eur: 6.39 } },
              { name: "Maker's Mark", price: { bgn: 12.5, eur: 6.39 } },
            ],
          },
          {
            name: 'Single malt whisky 50 ml',
            items: [
              { name: 'Glenfiddich 12 YO', price: { bgn: 20.0, eur: 10.23 } },
              { name: 'Talisker 10 YO', price: { bgn: 22.0, eur: 11.25 } },
              { name: 'Balvenie 12 YO', price: { bgn: 28.0, eur: 14.32 } },
              { name: 'Yamazakura', price: { bgn: 22.0, eur: 11.25 } },
            ],
          },
          {
            name: 'Rum 50 ml',
            items: [
              { name: 'Havana Club 3 YO', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Havana Club 7 YO', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Cocktails 200 ml',
            items: [
              { name: 'Aperol Spritz', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Campari & Tonic', price: { bgn: 9.5, eur: 4.86 } },
            ],
          },
          {
            name: 'Liqueurs 50 ml',
            items: [
              { name: 'Baileys', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Disaronno Amaretto', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Grand Marnier', price: { bgn: 18.0, eur: 9.2 } },
              { name: 'Martini', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Campari', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Drambuie', price: { bgn: 15.0, eur: 7.67 } },
              { name: 'Fernet-Branca', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Branca Menta', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Jägermeister', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Limoncello di Capri', price: { bgn: 7.5, eur: 3.83 } },
              { name: 'Benedictine', price: { bgn: 12.0, eur: 6.14 } },
            ],
          },
          {
            name: 'Tequila 50 ml',
            items: [{ name: 'Sauza Gold', price: { bgn: 7.5, eur: 3.83 } }],
          },
          {
            name: 'Cognac 50 ml',
            items: [
              { name: 'Courvoisier VS', price: { bgn: 16.0, eur: 8.18 } },
              { name: 'Pliska XO brandy', price: { bgn: 9.5, eur: 4.86 } },
              { name: 'Armagnac VSOP', price: { bgn: 18.0, eur: 9.2 } },
            ],
          },
          {
            name: 'Beer',
            items: [
              { name: 'Starobrno draught 300 ml', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Starobrno draught 500 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Heineken draught 250 ml', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Heineken draught 400 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Zagorka 330 ml', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Heineken 330 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Heineken 0.0 330 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Stolichno Weiss 330 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Birra Moretti 330 ml', price: { bgn: 6.9, eur: 3.53 } },
            ],
          },
        ],
      },
      {
        id: 'drinks',
        title: 'Beverages',
        image: {
          src: drinksImage,
          alt: 'Refreshing beverages served at CheckPoint Charly',
        },
        groups: [
          {
            name: 'Non-alcoholic',
            items: [
              { name: 'Coca-Cola, Fanta, tonic, soda 250 ml', price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Mineral water 500 ml', price: { bgn: 3.9, eur: 1.99 } },
              { name: 'Mineral water 1 L', price: { bgn: 5.9, eur: 3.02 } },
              { name: 'Perrier 330 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Perrier 750 ml', price: { bgn: 9.9, eur: 5.06 } },
              { name: 'Ayran 250 ml', price: { bgn: 6.9, eur: 3.53 } },
              { name: 'Natural juice 250 ml', price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Freshly squeezed juice 250 ml', price: { bgn: 7.9, eur: 4.04 } },
              { name: 'Homemade citronade 250 ml', price: { bgn: 7.9, eur: 4.04 } },
            ],
          },
          {
            name: 'Hot beverages',
            items: [
              { name: "ILLY espresso 30 ml", price: { bgn: 4.9, eur: 2.51 } },
              { name: 'Cappuccino 100 ml', price: { bgn: 5.9, eur: 3.02 } },
              { name: '“Delmar Te” tea 200 ml', price: { bgn: 4.9, eur: 2.51 } },
            ],
          },
        ],
      },
    ],
  },
};
